import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultPagination } from 'src/constants/main';
import client from 'src/services/client';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse, getCancelToken } from 'src/utils/helpers';
import { DateOfWeekMap } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { defaulPromobannerstFilter, defaultPromobanner, defaultPromobannersIsValid } from '../constants/promobanners';
import { IEditPromobanner, IPromobannersFilter, IPromobannersIsValid, PROMOBANNERR_TARGET, TPromobanner } from '../types/promobanners';

export class PromobannerStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<TPromobanner> = defaultPagination;
  @observable filters: IPromobannersFilter = defaulPromobannerstFilter;
  @observable promobanner: IEditPromobanner = defaultPromobanner;
  @observable isValid: IPromobannersIsValid = defaultPromobannersIsValid;
  @observable isEdit: boolean = false;
  @observable loading: boolean = false;

  @action fetchPromobanners = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchPromobanners';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      if (typeof page !== 'number') page = this.pagination.current_page + 1;

      this.pagination.loading = true;
      this.pagination.refreshing = true;

      const pagination = await client.get('/v3/promotion/banners/list', {
        params: {
          page,
          name: this.filters.name,
          end_date: this.filters.end_date,
          status: this.filters.status,
          is_delete: this.filters.is_delete,
          sort_field: this.filters.sort_field,
          sort_dir: this.filters.sort_dir,
        },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });
      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
        data: pagination.data,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'PromobannerStore fetchPromobanners' });
    }
  };

  @action getPromobanner = async (id: TPromobanner['id']) => {
    try {
      this.loading = true;

      const promobanner: TPromobanner = await client.get(`/v3/promotion/banners/${id}`);
      this.promobanner = this.setPromobanner(promobanner);
      // this.editablePromobanner = this.setPromobanner(promobanner);
      this.validator();

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'PromobannerStore getPromobanner', showModal: true });
    }
  };

  private setPromobanner = (promobanner: TPromobanner) => {
    return {
      name: promobanner.name,
      image: promobanner.image,
      start_date: moment(promobanner.start_date).unix(),
      end_date: moment(promobanner.end_date).unix(),
      category: promobanner.category,
      product: promobanner.item,
      target: this.getTarget(promobanner),
      link: promobanner.link,
      is_shown_as_offer: promobanner.is_shown_as_offer,
      active_24_hours: promobanner.active_24_hours,
      timeslots: promobanner.timeslots?.length ? promobanner.timeslots : defaultPromobanner.timeslots,
    };
  };

  private getTarget = ({ category, item, link }: TPromobanner) => {
    if (link) {
      return PROMOBANNERR_TARGET.LINK;
    } else if (item) {
      return PROMOBANNERR_TARGET.ITEM;
    } else if (category) {
      return PROMOBANNERR_TARGET.CATEGORY;
    }
    return PROMOBANNERR_TARGET.NONE;
  };

  @action editPromobanner = async (id: TPromobanner['id']) => {
    try {
      this.loading = true;

      await client.put(`/v3/promotion/banners/${id}`, this.getParams());
      // const promobanner: TPromobanner = await client.put(`/v3/promotion/banners/${id}`, this.promobanner);

      this.loading = false;
      // this.editablePromobanner = this.setPromobanner(promobanner);
      this.isEdit = false;
      this.fetchPromobanners(1);

      toastSuccess({ message: localeStore.t('toast_promobanner_changes_saved') });

      return true;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'PromobannerStore editPromobanner', showModal: true });
    }
  };

  private getParams = () => {
    return {
      name: this.promobanner.name,
      image: this.promobanner.image,
      start_date: moment.unix(this.promobanner.start_date || 0).format('YYYY-MM-DD'),
      end_date: this.promobanner.end_date
        ? moment.unix(this.promobanner.end_date).format('YYYY-MM-DD')
        : moment(moment().add(100, 'years')).format('YYYY-MM-DD'),
      category_id: this.promobanner.category?.id || null,
      product_id: this.promobanner.product?.id || null,
      link: this.promobanner?.link || null,
      is_shown_as_offer: this.promobanner?.is_shown_as_offer ?? false,
      timeslots: this.promobanner.timeslots?.reduce((acc, item) => {
        acc[DateOfWeekMap[item.weekday]] = { ...item };
        return acc;
      }, {} as Record<string, any>),
      active_24_hours: this.promobanner.active_24_hours,
    };
  };

  // private reset = () => {
  //   this.promobanner = this.editablePromobanner;
  //   this.isEdit = false;
  //   this.validator();
  // };

  @action deletePromobanner = async (id: number) => {
    try {
      this.loading = true;

      await client.delete(`/v3/promotion/banners/${id}`);

      this.loading = false;
      this.fetchPromobanners(1);
      this.isEdit = false;
      toastSuccess({ message: localeStore.t('toast_promobanner_deleted') });

      return true;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'PromobannerStore deletePromobanner', showModal: true });
    }
  };

  @action createPromobanner = async () => {
    try {
      this.loading = true;

      await client.post(`/v3/promotion/banners`, this.getParams());

      this.loading = false;
      this.isEdit = false;
      this.fetchPromobanners(1);

      toastSuccess({ message: localeStore.t('toast_promobanner_changes_saved') });

      return true;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'PromobannerStore createPromobanner', showModal: true });
    }
  };

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status } : item));

      await client.put(`/v3/promotion/banners/${id}`, { status }, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      toastSuccess({ message: localeStore.t('toast_promobanner_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status: booleanNumberReverse(item.status) } : item));
      logger.error({ error, place: 'PromobannerStore changeStatus' });
    }
  };

  @action setFilters = (filters: IPromobannersFilter, clear: boolean) => {
    if (clear) {
      this.filters = filters;
    } else {
      this.filters = { ...this.filters, ...filters };
    }

    this.fetchPromobanners(1);
  };

  @action changePromobanner = (promobanner: IEditPromobanner, validation?: boolean) => {
    this.promobanner = { ...this.promobanner, ...promobanner };
    this.isEdit = true;
    if (validation) {
      this.validator();
    }
  };

  @action validator = () => {
    // promobanner validation name
    if (this.promobanner.name) {
      this.isValid.name = true;
    } else this.isValid.name = false;
    // promobanner validation image
    if (this.promobanner.image) {
      this.isValid.image = true;
    } else this.isValid.image = false;
    // promobanner validation start_date
    if (this.promobanner.start_date) {
      this.isValid.start_date = true;
    } else this.isValid.start_date = false;
    // promobanber validation target
    if (this.promobanner.target) {
      //  promobanber validation category
      if (this.promobanner.target === PROMOBANNERR_TARGET.CATEGORY && !this.promobanner.category) {
        this.isValid.target = false;
      }
      //  promobanber validation item
      else if (this.promobanner.target === PROMOBANNERR_TARGET.ITEM && (!this.promobanner.category || !this.promobanner.product)) {
        this.isValid.target = false;
      }
      //  promobanber validation link
      else if (this.promobanner.target === PROMOBANNERR_TARGET.LINK && !this.promobanner.link) {
        this.isValid.target = false;
      } else {
        this.isValid.target = true;
      }
    } else {
      this.isValid.target = true;
    }
    // promobanner validation all
    if (this.isValid.name && this.isValid.image && this.isValid.start_date && this.isValid.target) {
      this.isValid.all = true;
    } else this.isValid.all = false;
  };

  @action unMount = () => {
    this.promobanner = defaultPromobanner;
    // this.editablePromobanner = defaultPromobanner;
    this.isEdit = false;
    this.isValid = defaultPromobannersIsValid;
  };
}

export const promobannerStore = new PromobannerStore();
